import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/workflow/organisms/Meta';
import { Terms } from '@bizapp-lp/workflow/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    note: {
      margin: '48px 0 0 0 !important',
    },
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '21px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
  }),
);

const title = 'Workflowサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Workflowサービス仕様書です。';

const ServiceSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>Workflowサービス仕様書</span>
      </>
    ),
    headerContent: '',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite Workflowサービス仕様書です。最新版は
            <Link href="https://www.worksap.co.jp/services/spec/wf-spec/">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Workflow（以下、
            Workflow）利用にあたり株式会社ワークスアプリケーションズ（以下、弊社）より提供可能なクラウドサービスの内容を記述したものです。Workflowの利用に際しては、本内容および「HUE
            Works Suite / HUE Works Suite DX
            Solutions利用規約」（以下、利用規約）に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と利用規約の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">2. Workflowサービス概要</Typography>
          <Typography variant="body1">
            Workflowは法人向けのワークフローサービスです。ノンプログラミングで簡単に申請経路を作成することができ、ペーパーレス化や業務効率の向上を支援します。
          </Typography>
          <Typography variant="body1">
            ※Workflowは法人のお客様向けサービスです。ご利用は法人（公的機関を含む）に限らせていただきます。
          </Typography>
          <Typography variant="body1">
            ※Workflowはお客様のグループ会社等に所属するユーザー（以下、外部ユーザー）と共同で利用することが可能です。ただし、外部ユーザーによるWorkflowの利用に関連する一切の行為はお客様による行為とみなすこととし、外部ユーザーによる利用規約または本書の違反についてはお客様による違反として、お客様が弊社に対して一切の責任を負うものとします。なお、弊社が、外部ユーザーが所属する法人の名称および所在地の開示を要請した場合には、お客様は弊社に対して遅滞なくこれらを書面にて通知するものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Workflowでは下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理者機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>サービス管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>処理フロー管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>トレイ管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>報告管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>発番管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>印影設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>データパイプライン管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ロール管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>組織管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>メール設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>IPアドレス制限</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>連携管理</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>申請</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>承認・差し戻し・却下・保留</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      書類情報表示（書類を一時保存、未処理、処理済みなどのステータスごとに表示）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>報告（申請内容を集計して報告）</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      データパイプライン（CSVファイル取り込みによりマスターを作成）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      通知（承認依頼時や承認完了時にベル通知とメール通知を送信）
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.2 Workflowオプションサービス</Typography>
            <Typography variant="body1">
              Workflowでは下記サービスをオプションサービスとして別途有償にて提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記サービスの仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>オプションサービス</TableCell>
                    <TableCell>内容</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      容量追加（10GB）
                    </TableCell>
                    <TableCell>
                      ストレージ容量を10GB単位で増量できるサービスです。標準サービスの容量制限（契約ライセンス数×3GB）を超えてWorkflowをご利用されたい場合は、本サービスにより容量を増量することができます。
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      管理者トレーニング
                    </TableCell>
                    <TableCell>
                      管理者向けの3時間のトレーニングプログラムを受講できるサービスです。Workflow全体の設定の流れ、組織やユーザー権限の登録手順、申請フォームの作成手順および処理フローの設計手順をハンズオン形式で体験することができます。
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      環境設定パック
                    </TableCell>
                    <TableCell>
                      「管理者トレーニング」に加えて、Workflowの利用開始に必要なマスタ設定およびセキュリティ設定（SSO設定、IP制御など）を弊社が代行するサービスです。設定するマスタの内容およびセキュリティの内容はお客様にご提供いただく必要があります。本サービスには、当該設定内容のヒアリング、マスタの設定および検証が含まれます。初期設定に不安がある方に推奨しています。
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      導入支援パック
                    </TableCell>
                    <TableCell>
                      「環境設定パック」に加えて、申請書1枚分の「フォーム作成代行」および処理フロー1本分の「フロー設計代行」が含まれるサービスです。短期で稼働したい方に推奨しています。
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      フォーム作成代行
                    </TableCell>
                    <TableCell>
                      申請フォームの作成を弊社が代行するサービスです。作成する申請フォームの内容はお客様にご提供いただく必要があります。本サービスには、当該フォーム内容のヒアリング、申請フォームの作成および検証が含まれます。
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      フロー設計代行
                    </TableCell>
                    <TableCell>
                      処理フローの設計および設定を弊社が代行するサービスです。設計する処理フローの内容はお客様にご提供いただく必要があります。本サービスには、当該フロー内容のヒアリング、処理フローの設計、設定および検証が含まれます。
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.3 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.4 提供時間帯およびサポートサービス
            </Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">2.4.1 提供時間帯</Typography>
              <Typography variant="body1">
                ・Workflowは、利用規約第8条第2項または第3項に定める場合を除き、原則として24時間365日ご利用いただけます。
              </Typography>
            </Container>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">2.4.2 サポートサービス</Typography>
              <Typography variant="body1">
                ・お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。
              </Typography>
              <Typography variant="body1">
                ・弊社によるサポートサービス（お問い合わせへのご回答）は、祝祭日及び弊社の休日を除く月曜日から金曜日までの午前9時30分から午後6時30分までの時間帯において提供いたします。なお、当該時間帯に受け付けましたお問い合わせについて当日中のご回答を保証するものではなく、3営業日をめどに1次回答いたします。
              </Typography>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 利用料金</Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【標準サービス】</Typography>
              <List component="ul" className={classes.innerList}>
                <ListItem>
                  <ListItemText>[月額契約の場合]</ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        月額利用料金：300円（税抜）/ライセンス
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        ※初回月額利用料金に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>[年額契約の場合]</ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        年額利用料金：3,000円（税抜）/ライセンス
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        ※月額利用料金の2か月分を割り引いた金額になります。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        ※初回年額利用料金に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <List component="ul" className={classes.note}>
                <ListItem>
                  <ListItemText>
                    「契約ライセンス数×3GB」の容量制限があります。容量制限は全ユーザーで利用できる総量となります。
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    利用料金は、契約ライセンス数に対して発生します。ライセンスは、アカウントが作成されると自動付与されます。
                  </ListItemText>
                </ListItem>
              </List>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Workflowライセンス
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          「Workflowに登録されているアカウントがライセンス対象となります。
                        </span>
                        <span style={{ display: 'block' }}>
                          契約ライセンス数を超えない限り、アカウントの登録・削除は同一月内に何度でも行うことができます。
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【オプションサービス】</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    容量追加（10GB）：1,000円（税抜）/月
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    管理者トレーニング：45,000円（税抜）/人
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>環境設定パック：150,000円（税抜）</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>導入支援パック：237,500円（税抜）</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    フォーム作成代行：20,000円（税抜）/申請書
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    フロー設計代行：67,500円（税抜）/フロー
                  </ListItemText>
                </ListItem>
              </List>
            </Container>
            <List component="ul" className={classes.noteList}>
              <ListItem>
                <ListItemText>
                  お客様のご契約ライセンス数によっては、上記料金にかかわらず別途お見積りによる対応となる場合があります。
                </ListItemText>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.6 契約期間</Typography>
            <Typography variant="body1">[月額契約の場合]</Typography>
            <Typography variant="body1">
              利用規約に定める本サービス利用開始日から当該利用開始日が属する月の末日までとします。但し、契約期間満了の1か月前までに、弊社所定の方法による契約終了の意思表示がなされない場合、同一条件により、1か月間更新されるものとし、以後も同様とします。
            </Typography>
            <Typography variant="body1">[年額契約の場合]</Typography>
            <Typography variant="body1">
              利用規約に定める本サービス利用開始日が月の初日の場合は本サービス利用開始日から1年間とし、本サービス利用開始日が月の途中の場合は本サービス利用開始日から当該利用開始日の1年後の日付が属する月の末日までとします。但し、契約期間満了の1か月前までに、当社所定の方法による契約終了の意思表示がなされない場合、同一条件により、利用契約は1年間更新されるものとし、以後も同様とします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.7 算定方法およびお支払い</Typography>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">2.7.1 算定方法</Typography>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【標準サービス】</Typography>
                <Typography variant="body1">[月額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      利用規約に定める本サービス利用開始日から、利用料金が発生します。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      毎月末時点の契約ライセンス数に応じて当月の利用料金を確定します。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      本サービス利用開始日が月の途中の場合、当該月の利用料金は日割り計算となります。
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>
                          当該月の利用料金 ＝{'{'}月額利用料金－（月額利用料金 ×
                          利用しない日数 ÷ 暦日数）{'}'} × 契約ライセンス数
                        </ListItemText>
                      </ListItem>
                      <ListItem>
                        <ListItemText>
                          ※当該月の初日から本サービス利用開始日の前日までの日数を「利用しない日数」とします。
                        </ListItemText>
                        <ListItemText>
                          ※日割り計算は先乗後除で行い、小数点は四捨五入とします
                        </ListItemText>
                      </ListItem>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              2022年1月16日に、100ライセンスの利用を開始する場合
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  2022年1月16日～同年1月31日（16日分）
                                </ListItemText>
                                <ListItemText>
                                  ＝｛300円－（300円×15日÷31日）｝×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝155円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝15,500円（税抜）</ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      月の途中でライセンス数を追加する場合、当該追加ライセンス数分の利用料金は日割り計算となります。
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>
                          追加ライセンス数分の利用料金 ＝{'{'}
                          月額利用料金－（月額利用料金 × 利用しない日数 ÷
                          暦日数）
                          {'}'} × 追加契約ライセンス数
                        </ListItemText>
                      </ListItem>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              4月1日から4月30日までの月額契約期間中の4月16日に、追加で100ライセンスの利用を開始する場合
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  2022年4月16日～同年4月30日（15日分）
                                </ListItemText>
                                <ListItemText>
                                  ＝｛300円－（300円×15日÷30日）｝×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝150円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝15,000円（税抜）</ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      利用規約の定めに従って契約期間の途中で利用契約が終了する場合であっても、当該終了日を含む月の利用料金は、日割り計算による減額または返金は行わず、上記に従って算出される当該月の利用料金全額をお支払いいただきます。
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography variant="body1">[年額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      利用規約に定める本サービス利用開始日から、利用料金が発生します。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      本サービス利用開始日が月の初日の場合、初回の契約期間は本サービス利用開始日から1年間となり、初回の利用料金は、年額利用料金として定める金額となります。
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <ListItemText>
                          2022年1月1日に100ライセンスの利用を開始する場合、初回の契約期間は2022年1月1日から同年12月31日までとなり、初回の利用料金は、年額利用料金にあたる3,000円（税抜）×100ライセンス＝300,000円（税抜）となります。
                        </ListItemText>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      本サービス利用開始日が月の途中の場合、初回の契約期間は本サービス利用開始日から当該利用開始日の1年後の日付が属する月の末日までとなり、初回の利用料金は、当該利用開始日が属する月の利用料金（月額利用料金を日割り計算した金額（算定方法は月額契約における日割り計算と同様））を、年額利用料金に加算した金額となります。
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              2022年1月16日に、100ライセンスの利用を開始する場合（初回の契約期間は2022年1月16日から2023年1月31日までとなり、2回目以降の契約期間は毎年2月1日から翌年1月31日までとなります）
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  ①2022年1月16日～同年1月31日（16日分）
                                </ListItemText>
                                <ListItemText>
                                  ＝｛月額利用料金－(月額利用料金×利用しない日数÷暦日数）｝×2か月分の割引率×契約ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝｛300円－（300円×15日÷31日）｝×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝155円×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝129円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝12,900円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  ②2022年2月1日～2023年1月31日（12か月分）
                                </ListItemText>
                                <ListItemText>
                                  ＝年額利用料金×契約ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝3,000円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝300,000円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  従って、初回の利用料金＝①＋②＝312,900円（税抜）
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      2回目以降の契約期間は、前回の契約期間満了日の翌日から1年間となり、当該契約期間に係る利用料金は、現行の契約期間満了日が属する月（以下、契約期間満了月）の前月末日時点の契約ライセンス数に応じて次回の契約期間に係る利用料金を確定します。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      本サービス利用開始日後にライセンス数を追加する場合、当該追加ライセンス数に係る初回の利用料金は、当該追加月の利用料金（月の途中で追加した場合は月額利用料金を日割り計算した金額（算定方法は月額契約における日割り計算と同様））に2か月分の割引率を乗じた金額（小数点は四捨五入）を、月額利用料金に当該追加月の翌月から契約期間満了月まで月数を乗じて2か月分の割引率を乗じた金額（小数点は四捨五入）に加算した金額を一括でお支払いいただきます
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例1】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              [追加月が、契約期間満了月以外の場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  2022年1月1日から同年12月31日までの年額契約期間中の同年4月16日に、追加で100ライセンスの利用を開始する場合
                                </ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  ①2022年4月16日～同年4月30日（15日分）
                                </ListItemText>
                                <ListItemText>
                                  ＝｛月額利用料金－(月額利用料金×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝｛300円－（300円×15日÷30日）｝×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝150円×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝125円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝12,500円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  ②2022年5月1日～同年12月31日（8か月分）
                                </ListItemText>
                                <ListItemText>
                                  ＝（月額利用料金×利用月数）×2か月分の割引率×追加ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝2,000円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝200,000円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  追加ライセンス数に係る初回の利用料金＝①＋②＝212,500円（税抜）
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                        <ListItemText>【例2】</ListItemText>
                        <List>
                          <ListItem>
                            <ListItemText>
                              [追加月が、契約期間満了月に該当する場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  2022年1月1日から同年12月31日までの年額契約期間中の2022年12月16日に、追加で100ライセンスの利用を開始する場合
                                </ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  ①2022年12月16日～同年12月31日（16日分）
                                </ListItemText>
                                <ListItemText>
                                  ＝｛月額利用料金－(月額利用料金×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝｛300円－（300円×15日÷31日）｝×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝150円×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝155円×（10か月÷12か月）×100ライセンス
                                </ListItemText>
                                <ListItemText>
                                  ＝129円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝12,900円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  ②2023年1月1日～同年12月31日（12か月分）
                                </ListItemText>
                                <ListItemText>
                                  ＝年額利用料金×契約ライセンス数
                                </ListItemText>
                                <ListItemText>
                                  ＝3,000円×100ライセンス
                                </ListItemText>
                                <ListItemText>＝300,000円（税抜）</ListItemText>
                              </ListItem>
                              <ListItem>
                                <ListItemText>
                                  追加ライセンス数に係る初回の利用料金＝①＋②＝312,900円（税抜）
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      利用規約の定めに従って契約期間の途中で利用契約が終了する場合であっても、年額利用料金は、日割り計算による減額または返金は行わず、上記に従って算出される年額利用料金全額をお支払いいただきます。
                    </ListItemText>
                  </ListItem>
                </List>
              </Container>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【オプションサービス】</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      容量追加（10GB）を月の途中で行う場合であっても、当該月の料金は日割り計算による減額又または返金は行わず、月額　料金全額（1,000円（税抜））をお支払いいただきます。
                    </ListItemText>
                  </ListItem>
                </List>
              </Container>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">2.7.2 お支払い</Typography>
              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【請求書支払】</Typography>

                <Container disableGutters maxWidth={false}>
                  <Typography variant="h6">【標準サービス】</Typography>

                  <Typography variant="body1">[月額契約の場合]</Typography>
                  <List component="ul">
                    <ListItem>
                      <ListItemText>
                        毎月末日を締め日として、翌月末日にお支払いいただきます（後払い）。
                      </ListItemText>
                    </ListItem>
                  </List>

                  <Typography variant="body1">[年額契約の場合]</Typography>
                  <List component="ul">
                    <ListItem>
                      <ListItemText>
                        初回の契約期間に係る利用料金の支払期日は、①本サービス利用開始日が月の初日の場合は当月末日、②本サービス利用開始日が月の途中の場合は翌月末日となります。
                      </ListItemText>
                      <List component="ul" className={classes.innerList}>
                        <ListItem>
                          <ListItemText>【例】</ListItemText>
                          <List component="ul">
                            <ListItem>
                              <ListItemText>
                                本サービス利用開始日が2022年1月1日の場合、初回の契約期間に係る利用料金の支払期日は2022年1月末日となります。
                              </ListItemText>
                            </ListItem>
                            <ListItem>
                              <ListItemText>
                                本サービス利用開始日が2022年1月2日の場合、初回の契約期間に係る利用料金の支払期日は2022年2月末日となります。
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        2回目以降の契約期間に係る利用料金の支払期日は、契約期間満了月の前月末日を締め日として、契約期間満了月の末日に次回の契約期間に係る利用料金をお支払いいただきます（前払い）。
                      </ListItemText>
                      <List component="ul" className={classes.innerList}>
                        <ListItem>
                          <ListItemText>【例】</ListItemText>
                          <List component="ul">
                            <ListItem>
                              <ListItemText>
                                現行の契約期間が2022年1月1日から同年12月31日までの場合、契約期間満了月の前月にあたる2022年11月末日を締め日として利用料金が確定し、契約期間満了月にあたる2022年12月末日までに次回の契約期間（2023年1月1日から同年12月31日まで）にかかる利用料金をお支払いいただきます。
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        追加ライセンス数に係る初回の利用料金の支払期日は、当該追加月の翌月末日となります。2回目以降は、既存ライセンス数と合算して、契約期間満了月の前月末日を締め日として、契約期間満了月の末日にお支払いいただきます（前払い）。
                      </ListItemText>
                      <List component="ul" className={classes.innerList}>
                        <ListItem>
                          <ListItemText>【例1】</ListItemText>
                          <List component="ul">
                            <ListItem>
                              <ListItemText>
                                [追加月が、契約期間満了月以外の場合]
                              </ListItemText>
                              <ListItemText>
                                契約期間が2022年1月1日から同年12月31日までで既に500ライセンスを利用している場合、2022年10月10日に200ライセンスを追加すると、追加する200ライセンスに係る初回の利用料金（2022年10月分の日割り＋2022年11月～同年12月分）の支払期日は2022年11月末日となります。2回目のお支払いは、2023年度分の年額利用料金を、既存500ライセンスの2023年度分のお支払いと合算して2022年12月末日にお支払いいただき、以降も同様とします。
                              </ListItemText>
                            </ListItem>
                          </List>
                          <ListItemText>【例2】</ListItemText>
                          <List component="ul">
                            <ListItem>
                              <ListItemText>
                                [追加月が、契約期間満了月に該当する場合]
                              </ListItemText>
                              <ListItemText>
                                契約期間が2022年1月1日から2022年12月31日までで既に500ライセンスを利用している場合、2022年12月10日に200ライセンスを追加すると、追加する200ライセンスに係る初回の利用料金（2022年12月分の日割り＋2023年1月～同年12月分（2023年度分））の支払期日は2023年1月末日となります。2回目のお支払いは、2024年度分の年額利用料金を、既存500ライセンスの2024年度分のお支払いと合算して2023年12月末日にお支払いいただき、以降も同様とします。
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </Container>

                <Container disableGutters maxWidth={false}>
                  <Typography variant="h6">【オプションサービス】</Typography>

                  <List component="ul">
                    <ListItem>
                      <ListItemText>容量追加（10GB）</ListItemText>
                      <ListItemText>
                        毎月末日を締め日として、翌月末日にお支払いいただきます（後払い）。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>管理者トレーニング</ListItemText>
                      <ListItemText>
                        発注月の翌月末日に一括でお支払いいただきます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>環境設定パック</ListItemText>
                      <ListItemText>
                        発注月の翌月末日に一括でお支払いいただきます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>導入支援パック</ListItemText>
                      <ListItemText>
                        発注月の翌月末日に一括でお支払いいただきます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>フォーム作成代行</ListItemText>
                      <ListItemText>
                        発注月の翌月末日に一括でお支払いいただきます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>フロー設計代行</ListItemText>
                      <ListItemText>
                        発注月の翌月末日に一括でお支払いいただきます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </Container>

                <Typography variant="body1">
                  ※請求はマネーフォワードケッサイ株式会社による「マネーフォワード
                  ケッサイ」サービスにて行われます
                </Typography>
              </Container>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.8 提供条件</Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>
                  Workflowの提供に際しては、GrapeCity,Inc.（以下「GC」といいます。）が提供するSpreadJSが利用されます。
                  お客様は、Workflowを利用するにあたり、GC所定の「End-User
                  License Agreement For GrapeCity SpreadJS
                  Software」（その名称または内容に変更が生じた場合には、当該変更後の名称および内容によるものとします。
                  なお、最新版は{' '}
                  <Link href="https://www.grapecity.com/legal/eula">
                    https://www.grapecity.com/legal/eula
                  </Link>{' '}
                  に掲載されています。）のうちSpreadJSを利用して開発されたソフトウェアのユーザーが遵守すべき事項について同意し、これを遵守するものとします。
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  お客様は、Workflowに搭載する第三者サービスとの連携機能（以下「第三者サービス連携」といいます。）を利用するに際しては、当該第三者サービスを提供する事業者所定の条件（なお、当該条件は必要に応じ適宜更新されます。）について同意するものとします。なお、第三者サービス連携は、弊社と当該第三者サービスを提供する事業者との間の提携、協調、授権その他の一切の協力関係を意味するものではなく、お客様は、自己の責任において利用するものとします。また、お客様は、弊社が第三者サービス連携の提供、不提供、提供不能、契約期間中の提供終了または提供内容その他の一切の事項に関して如何なる責任もお客様に対し負担しないことおよび第三者サービス連携に起因するお客様と第三者との間の紛争その他一切の債権債務関係について、弊社に何ら迷惑をかけないことを予め異議なく承諾するものとします。
                </ListItemText>
              </ListItem>
            </List>
          </Container>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">3. 商標について</Typography>

          <Typography variant="body1">
            本書に掲載されている各社名あるいは各製品名は、各社の登録商標または商標です。
          </Typography>
          <List component="ul">
            <ListItem>
              <ListItemText>
                GrapeCityおよびSpreadJSはグレープシティ株式会社の登録商標または商標です。
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                マネーフォワードケッサイは、マネーフォワード株式会社の登録商標または商標です。
              </ListItemText>
            </ListItem>
          </List>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">付則</Typography>
          <Typography>
            改正後の本書は、2023年8月1日より効力を有するものとします。
          </Typography>
          <Typography className={classes.lineBreak}>
            <span>初版施行日：2021年7月7日</span>
            <span>
              <Link
                component={RouterLink}
                to="/workflow/spec-archive/service-spec-01-20210712"
              >
                第1回改正施行日：2021年7月12日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/workflow/spec-archive/service-spec-02-20220307"
              >
                第2回改正施行日：2022年3月7日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/workflow/spec-archive/service-spec-03-20220425"
              >
                第3回改正施行日：2022年4月25日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/workflow/spec-archive/service-spec-04-20220627"
              >
                第4回改正施行日：2022年6月27日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/workflow/spec-archive/service-spec-05-20230401"
              >
                第5回改正施行日：2023年4月1日
              </Link>
            </span>
            <span>第6回改正施行日：2023年8月1日</span>
          </Typography>
        </Container>
      </Terms>
    </>
  );
};

export default ServiceSpec;
